import React, { useState, useEffect } from 'react';
import { neonQuery } from '../services/api';
import { toTitleCase } from '../utils/format';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PAGE_SIZE = 50;

export default function VipSearch({ navigateTo }) {
  const [q, setQ] = useState('');
  const [members, setMembers] = useState(null);
  const [orders, setOrders] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(null);

  // Load total VIP count on mount
  useEffect(() => {
    neonQuery('vip_customer_count', {})
      .then(rows => setTotalCount(Number(rows[0]?.total ?? 0)))
      .catch(() => {});
    // Load recent VIPs on mount
    loadMembers('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMembers = async (query) => {
    setLoading(true);
    setError(null);
    setMembers(null);
    setSelectedMember(null);
    setOrders(null);
    try {
      const rows = await neonQuery('vip_customers', { q: query, limit: PAGE_SIZE });
      setMembers(rows);
      if (rows.length === 1) selectMember(rows[0]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const selectMember = async (member) => {
    setSelectedMember(member);
    setHistoryOpen(false);
    setTimelineLoading(true);
    neonQuery('vip_member_timeline', { customerId: member.id })
      .then(setTimeline)
      .catch(() => setTimeline([]))
      .finally(() => setTimelineLoading(false));
    setOrdersLoading(true);
    try {
      const rows = await neonQuery('orders_by_customer', { customerId: member.id });
      setOrders(rows);
    } catch (e) {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSearch = () => loadMembers(q);
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };
  const handleClear = () => {
    setQ('');
    setSelectedMember(null);
    setOrders(null);
    loadMembers('');
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
          VIP Members
          {totalCount !== null && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: 'var(--gray-500)' }}>
              {totalCount.toLocaleString()} in Neon
            </span>
          )}
        </h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Search VIP Members</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>NAME OR EMAIL</div>
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search VIP members..."
              className="form-input"
              style={{ fontSize: 12, width: '100%' }}
              autoFocus
            />
          </div>
          <button onClick={handleSearch} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
          <button onClick={handleClear} className="btn btn-navy" style={{ fontSize: 11 }}>All</button>
        </div>
      </div>

      {error && <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
      {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}

      {/* Member list */}
      {!loading && members !== null && !selectedMember && (
        <div className="card">
          <div className="card-header">
            {members.length === 0 ? 'No VIP members found' : `${members.length}${members.length >= PAGE_SIZE ? '+' : ''} VIP member${members.length !== 1 ? 's' : ''}`}
          </div>
          {members.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Member</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Phone</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Lifetime</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Last Order</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr
                      key={m.id}
                      onClick={() => selectMember(m)}
                      style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fff8f0'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : 'var(--gray-50)'}
                    >
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontWeight: 700, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, background: 'var(--orange)', color: 'white', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>VIP</span>
                          {toTitleCase([m.first_name, m.last_name].filter(Boolean).join(' ') || '—')}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>#{m.id}</div>
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--orange)' }}>{m.email}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{m.phone ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700 }}>{Number(m.order_count).toLocaleString()}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--green)', fontWeight: 600 }}>{fmtCurrency(m.lifetime_value)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtDate(m.last_order_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Selected member detail */}
      {selectedMember && (
        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <a onClick={() => { setSelectedMember(null); setOrders(null); setTimeline(null); }}
                style={{ cursor: 'pointer', color: 'var(--orange)', fontSize: 12, fontWeight: 600 }}>← Back</a>
              <span>VIP Member #{selectedMember.id}</span>
            </div>
            <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, fontSize: 12 }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, background: 'var(--orange)', color: 'white', padding: '1px 8px', borderRadius: 8, fontWeight: 700 }}>VIP</span>
                  {toTitleCase([selectedMember.first_name, selectedMember.last_name].filter(Boolean).join(' ') || '—')}
                </div>
                <div style={{ color: 'var(--orange)', marginTop: 4 }}>{selectedMember.email}</div>
                {selectedMember.phone && <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>{selectedMember.phone}</div>}
              </div>
              <div className="history-stat"><label>Total Orders</label><span>{Number(selectedMember.order_count).toLocaleString()}</span></div>
              <div className="history-stat"><label>Lifetime Value</label><span style={{ color: 'var(--green)' }}>{fmtCurrency(selectedMember.lifetime_value)}</span></div>
              <div className="history-stat"><label>Last Order</label><span>{fmtDate(selectedMember.last_order_date)}</span></div>
            </div>
          </div>

          {/* ── MEMBERSHIP HISTORY ──────────────────────────────────────────
              The whole point: a VIP's life in the club including the BREAKS.
              GroundShippingMembers cannot show this -- it holds one mutable row per
              customer, so a lapse-and-return looks identical to unbroken membership. */}
          <div className="card" style={{ marginBottom: 12 }}>
            <div
              className="card-header"
              onClick={() => setHistoryOpen(o => !o)}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <span>Membership History</span>
              {/* Keep the shape of the membership visible while collapsed, so the
                  header is worth reading on its own rather than a blind toggle. */}
              {timeline && timeline.length > 0 && (() => {
                const renewals = timeline.filter(e => e.event_type === "Renewed").length;
                const breaks = timeline.filter(e => e.event_type === "Re-Joined").length;
                return (
                  <span style={{ fontSize: 11, fontWeight: 400, color: "var(--gray-500)" }}>
                    {renewals} renewal{renewals === 1 ? "" : "s"}
                    {breaks > 0 && " · " + breaks + " break" + (breaks === 1 ? "" : "s")}
                  </span>
                );
              })()}
              <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--gray-500)" }}>{historyOpen ? "▲" : "▼"}</span>
            </div>
            {historyOpen && timelineLoading && <div style={{ padding: 16, fontSize: 12, color: "var(--gray-500)" }}>Loading history...</div>}
            {historyOpen && !timelineLoading && timeline !== null && timeline.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No membership history recorded.</div>
            )}
            {historyOpen && !timelineLoading && timeline && timeline.length > 0 && (() => {
              // Oldest first so the story reads forward; the query returns newest first.
              const evs = [...timeline].reverse();
              const STYLE = {
                'Joined':          { color: '#1a8a4a', dot: '#1a8a4a', label: 'Joined' },
                'Re-Joined':       { color: '#1a8a4a', dot: '#1a8a4a', label: 'Re-joined' },
                'Renewed':         { color: 'var(--gray-500)', dot: '#c8cdd2', label: 'Renewed' },
                'Lapsed':          { color: '#c0392b', dot: '#c0392b', label: 'Lapsed' },
                'Auto Renew Off':  { color: '#e67e22', dot: '#e67e22', label: 'Turned off auto-renew' },
                'Auto Renew On':   { color: '#1a8a4a', dot: '#7bc47f', label: 'Auto-renew back on' },
              };
              const renewals = evs.filter(e => e.event_type === 'Renewed').length;
              const breaks = evs.filter(e => e.event_type === 'Re-Joined').length;
              const first = evs[0];
              const isActive = evs[evs.length - 1].event_type !== 'Lapsed';

              return (
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 12 }}>
                    <span>Member since <b style={{ color: 'var(--navy)' }}>{fmtDate(first.event_date)}</b></span>
                    <span><b style={{ color: 'var(--navy)' }}>{renewals}</b> renewal{renewals === 1 ? '' : 's'}</span>
                    <span><b style={{ color: breaks ? '#c0392b' : 'var(--navy)' }}>{breaks}</b> break{breaks === 1 ? '' : 's'} in membership</span>
                    <span style={{ color: isActive ? '#1a8a4a' : '#c0392b', fontWeight: 700 }}>{isActive ? 'Currently active' : 'Not currently active'}</span>
                  </div>

                  <div style={{ position: 'relative', paddingLeft: 18 }}>
                    <div style={{ position: 'absolute', left: 4, top: 4, bottom: 4, width: 2, background: 'var(--gray-200)' }} />
                    {evs.map((e, i) => {
                      const st = STYLE[e.event_type] || { color: 'var(--gray-500)', dot: '#c8cdd2', label: e.event_type };
                      // A gap is the elapsed time between lapsing and coming back —
                      // the thing GSM destroys and the reason this panel exists.
                      let gap = null;
                      if (e.event_type === 'Re-Joined' && i > 0) {
                        const prev = evs[i - 1];
                        if (prev.event_type === 'Lapsed' || prev.event_type === 'Auto Renew Off') {
                          const days = Math.round((new Date(e.event_date) - new Date(prev.event_date)) / 86400000);
                          if (days > 40) {
                            gap = days >= 365
                              ? (days / 365).toFixed(1) + ' years away'
                              : Math.round(days / 30) + ' months away';
                          }
                        }
                      }
                      return (
                        <div key={i}>
                          {gap && (
                            <div style={{ margin: '4px 0 4px -18px', paddingLeft: 18, fontSize: 11, color: '#c0392b', fontStyle: 'italic' }}>
                              ⋯ {gap}
                            </div>
                          )}
                          <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 10, padding: '3px 0' }}>
                            <span style={{ position: 'absolute', left: -18, top: 7, width: 10, height: 10, borderRadius: 5, background: st.dot, border: '2px solid #fff' }} />
                            <span style={{ fontSize: 11.5, color: 'var(--gray-500)', minWidth: 78 }}>{fmtDate(e.event_date)}</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: st.color, minWidth: 150 }}>{st.label}</span>
                            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                              {e.evidence_type === 'Order' && e.evidence_id ? 'order ' + e.evidence_id
                                : e.evidence_type === 'Renewal Payment' ? 'renewal charge'
                                : e.evidence_type === 'Derived' ? (e.note || 'derived')
                                : e.evidence_type === 'Membership Record' ? (e.note || 'membership record')
                                : ''}
                            </span>
                            {/* Storefront auto-renew changes carry where they came from; the browser is on hover. */}
                            {(e.client_ip || Number(e.reward_cash_forfeited) > 0) && (
                              <span style={{ fontSize: 11, color: 'var(--gray-500)' }} title={e.user_agent || ''}>
                                {[e.client_ip ? 'from ' + e.client_ip : null,
                                  Number(e.reward_cash_forfeited) > 0 ? '$' + Number(e.reward_cash_forfeited).toFixed(2) + ' Reward Cash forfeited' : null]
                                  .filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="card">
            <div className="card-header">Order History</div>
            {ordersLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading orders...</div>}
            {!ordersLoading && orders !== null && orders.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders found.</div>
            )}
            {!ordersLoading && orders && orders.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order #</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Method</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Tracking</th>
                      <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((row, i) => (
                      <tr
                        key={i}
                        style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}
                        onClick={() => navigateTo && navigateTo('order-details', String(row.bc_order_id ?? row.id))}
                      >
                        <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bc_order_id ?? row.id ?? '—'}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{fmtDate(row.order_date)}</td>
                        <td style={{ padding: '8px 8px' }}>
                          <span className={`order-status-badge status-${(row.status || 'processing').toLowerCase()}`}>{row.status || '—'}</span>
                        </td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.ship_method ?? '—'}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.tracking_number ?? '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(row.order_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
